import { useOidc } from '@axa-fr/react-oidc';
import {
  clearAllUserSession,
  IconButton,
  parseHtml,
  useMessageChannelContext,
  useModalManager,
} from '@healthhub/react-ui';
import { Modal, Spin, Typography } from 'antd';
import type { ReactNode } from 'react';
import { memo, useCallback, useEffect, useState } from 'react';

import { FilledCloseIcon } from '@shared/assets-icon/Filled';
import { Button } from '@shared/components';
import { sitecoreHomeBaseUrl } from '@shared/configs/configs/config';
import { TERM_OF_USES_CODE, TERM_OF_USES_TITLE_CODE } from '@shared/constants/constants';
import { TOU_CONSENTS } from '@shared/constants/touConsents';
import { useLayoutViewContext } from '@shared/context';
import useIsAuthenticated from '@shared/hooks/useIsAuthenticated';
import { useGetMessageSettingsQuery } from '@shared/services/systemSettings';
import { useGetTermsOfUseQuery, useUpdateTermsOfUseMutation } from '@shared/services/termsOfUse';
import { persistor } from '@shared/stores';
import { MESSAGE_ACTION } from '@shared/utils/bridge';
import { clearCookies } from '@shared/utils/cookie';

const { Text } = Typography;

const TermsOfUseModal = () => {
  const { isPortalView } = useLayoutViewContext();
  const { sendMessageToNative } = useMessageChannelContext();
  const { logout } = useOidc();
  const { isAuthenticated } = useIsAuthenticated();
  const [isOpen, setIsOpen] = useState(false);
  const { showErrorModal } = useModalManager();
  const { data: termOfUse, isFetching: isFetchingTermOfUse } = useGetTermsOfUseQuery(
    { TouType: TOU_CONSENTS.healthhub },
    { skip: !isAuthenticated }
  );
  const [updateTermsOfUse, { isLoading: isUpdatingTermsOfUse }] = useUpdateTermsOfUseMutation();

  const {
    data: termOfUseData,
    isFetching: isFetchingTermOfUseContent,
    error: termsOfUseError,
    refetch: refetchTermsOfUse,
  } = useGetMessageSettingsQuery(
    { Codes: [TERM_OF_USES_CODE, TERM_OF_USES_TITLE_CODE] },
    { skip: !isAuthenticated || termOfUse?.Consents !== false }
  );

  const termOfUseTitleData = termOfUseData?.find((item) => item.Code === TERM_OF_USES_TITLE_CODE);
  const termOfUseContentData = termOfUseData?.find((item) => item.Code === TERM_OF_USES_CODE);

  const onClickCancel = useCallback(async () => {
    setIsOpen(false);
    clearAllUserSession({ persistor, clearCookies });
    await logout(sitecoreHomeBaseUrl());

    if (!isPortalView) {
      sendMessageToNative(MESSAGE_ACTION.unauthorised());
    }
  }, [isPortalView, logout, sendMessageToNative]);

  const showGenericErrorModal = useCallback(
    (title: string, description: ReactNode, onRefresh?: () => void) => {
      showErrorModal({
        cancelButton: { onClick: onClickCancel, text: 'Cancel' },
        content: [{ description, title }, { description: 'If the issue persists, please try again later.' }],
        disableKeyboardClose: true,
        okButton: { onClick: onRefresh, text: 'Refresh' },
      });
    },
    [onClickCancel, showErrorModal]
  );

  const onClickAccept = async () => {
    const showSaveErrorModal = () => {
      showGenericErrorModal(
        'Error Saving',
        <p className="p2">
          We couldn&apos;t save your acceptance. Tap <span className="font-semibold">&rsquo;Refresh&rsquo;</span> to try
          again.
        </p>
      );
    };

    try {
      await updateTermsOfUse({
        TouType: TOU_CONSENTS.healthhub,
      }).unwrap();
      setIsOpen(false);
    } catch {
      showSaveErrorModal();
    }
  };

  useEffect(() => {
    if (termOfUse?.Consents === false && !isFetchingTermOfUse && !isFetchingTermOfUseContent) {
      setIsOpen(true);

      if (termsOfUseError || !termOfUseContentData || !termOfUseTitleData) {
        showGenericErrorModal(
          'Error Loading Terms of Use',
          <p className="p2">
            We couldn&apos;t load the terms of use right now. Tap{' '}
            <span className="font-semibold">&rsquo;Refresh&rsquo;</span> to try again.
          </p>,
          refetchTermsOfUse
        );
      }
    }
  }, [
    isFetchingTermOfUse,
    isFetchingTermOfUseContent,
    refetchTermsOfUse,
    showGenericErrorModal,
    termOfUse,
    termsOfUseError,
    termOfUseContentData,
    termOfUseTitleData,
  ]);

  if (!isAuthenticated) return null;

  return (
    <Modal
      centered
      className="max-w-256 [&_.ant-modal-body]:h-[calc(100dvh-1rem)] [&_.ant-modal-body]:rounded-2xl  [&_.ant-modal-body]:bg-secondary-yellow"
      closeIcon={
        <IconButton
          aria-label="Close"
          icon={<FilledCloseIcon className="absolute right-4 top-4 size-5" />}
          onClick={onClickCancel}
        />
      }
      destroyOnHidden
      footer={null}
      open={isOpen}
      width="100%"
    >
      {isFetchingTermOfUseContent ? (
        <div className="flex h-full items-center justify-center">
          <Spin />
        </div>
      ) : (
        <section className="flex h-full flex-col gap-y-8">
          <section className="flex flex-col gap-y-4 text-center">
            <Text className="text-2xl font-semibold">{termOfUseTitleData?.Title}</Text>
            <Text className="text-base">{termOfUseTitleData?.Value}</Text>
          </section>
          <section className="flex-1 overflow-auto rounded-lg border border-theme-700 bg-white px-8 pt-10">
            {parseHtml(termOfUseContentData?.Value ?? '')}
          </section>
          <section className="flex justify-center">
            <Button
              className="w-full text-balance bg-primary-blue-grey-3  lg:max-w-[490px] [&_span]:text-white"
              loading={isUpdatingTermsOfUse}
              onClick={onClickAccept}
              variant="primary"
            >
              Accept and continue
            </Button>
          </section>
        </section>
      )}
    </Modal>
  );
};

export default memo(TermsOfUseModal);
